import { useBrandLocale } from '../../hooks/useBrandLocale';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useApolloClient } from '@apollo/client';
import { toast } from 'react-toastify';
import { GEOCODE_ADDRESS } from '../../services/geocoding/queries';
import { useBackendPlaces } from '../../hooks/useBackendPlaces';
import { usePlacePredictions, type PlacePrediction } from '../../hooks/usePlacePredictions';

const InputContainer = styled.div`
  ${tw`relative w-full`}
`;

const InputWrapper = styled.div`
  ${tw`
    h-[44px]
    rounded-lg
    pl-3
    pr-3
    flex
    items-center
    cursor-text
    bg-white
  `}
  border: none;
`;

const AddressInputField = styled.input.attrs<{ horizontal?: boolean }>(props => ({
  horizontal: props.horizontal,
}))<{ horizontal?: boolean }>`
  ${tw`
    flex-1
    outline-none
    text-base
    text-text-primary
    ml-2
  `}
  border: none;
  /* Keep >= 16px on mobile to avoid iOS Safari focus auto-zoom */
  font-size: 16px;
  ${props =>
    props.horizontal &&
    `
    @media (min-width: 768px) {
      font-size: 0.7em;
    }
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const Icon = styled.span`
  ${tw`
    text-red-500
    fill-current
    text-xs
    md:text-base
  `};
`;

const ClearButton = styled.button`
  ${tw`
    text-text-tertiary
    cursor-pointer
    ml-2
    p-1
  `}

  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const SuggestionsContainer = styled.div`
  ${tw`
    absolute
    top-full
    left-0
    right-0
    bg-white
    shadow-card-hover
    rounded-md
    mt-1
    max-h-60
    overflow-y-auto
    z-[9999]
    border
    border-border-default
  `}
`;

const SuggestionItem = styled.div`
  ${tw`
    p-3
    cursor-pointer
    border-b
    border-border-default
    last:border-b-0
  `}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const SuggestionMainText = styled.div`
  ${tw`
    text-sm
    md:text-base
    text-text-primary
    font-medium
  `}
`;

const SuggestionSecondaryText = styled.div`
  ${tw`
    text-xs
    md:text-sm
    text-text-secondary
    mt-1
  `}
`;

export interface AutocompleteSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface LocationData {
  address: string;
  latitude: number;
  longitude: number;
  formattedAddress: string;
  zipCode?: string | null;
  timezone?: string | null;
  currentLocalTimeISO?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface AddressInputProps {
  value: string;
  placeholder?: string;
  onLocationSelect: (location: LocationData) => void;
  onLocationChange?: (address: string) => void;
  isLoading?: boolean;
  horizontal?: boolean;
}

/**
 * Address search for host forms (new location, car location, a place's
 * address, hot locations).
 *
 * Suggestions come from Google Places in the browser while the host types
 * (`usePlacePredictions`, like the guest search), so typing never waits for
 * our API; the API's proxy is only a fallback when the browser cannot use
 * Places. A pick is geocoded once by the API (`geocodeAddress`), which
 * returns the listing time zone and local time the forms save.
 *
 * The field keeps its own text, so a parent that passes a fixed `value`
 * (or no `onLocationChange`) still gets a field the host can type in.
 */
export function AddressInput({
  value,
  placeholder = 'Enter an address...',
  onLocationSelect,
  onLocationChange,
  isLoading = false,
  horizontal = false,
}: AddressInputProps) {
  const client = useApolloClient();
  const fallback = useBackendPlaces();
  // Spec 028: a Canadian brand's hosts pick Canadian addresses; US brands stay unrestricted.
  const { country } = useBrandLocale();
  const places = usePlacePredictions({
    debounceMs: 150,
    minLength: 3,
    fallback,
    countries: country === 'CA' ? ['ca'] : undefined,
  });
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [geocoding, setGeocoding] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // A new value from the parent (a saved address, a reset) replaces the text.
  useEffect(() => setText(value), [value]);

  useEffect(() => {
    const outside = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
    };
  }, []);

  const type = (next: string) => {
    setText(next);
    onLocationChange?.(next);
    setActiveIndex(-1);
    setOpen(true);
    places.search(next);
  };

  const pick = async (prediction: PlacePrediction) => {
    setText(prediction.description);
    onLocationChange?.(prediction.description);
    setOpen(false);
    places.clear();
    // Phones: drop the keyboard and the focus zoom once the address is chosen.
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      (document.activeElement as HTMLElement | null)?.blur();
    }
    setGeocoding(true);
    try {
      const { data } = await client.query({
        query: GEOCODE_ADDRESS,
        variables: { address: prediction.description },
        fetchPolicy: 'network-only',
      });
      const result = data?.geocodeAddress;
      if (!result) throw new Error('No geocode result');
      onLocationSelect({
        address: prediction.description,
        latitude: result.latitude,
        longitude: result.longitude,
        formattedAddress: result.formattedAddress,
        zipCode: result.zipCode ?? undefined,
        timezone: result.timezone ?? undefined,
        currentLocalTimeISO: result.currentLocalTimeISO ?? undefined,
        city: result.city ?? undefined,
        state: result.state ?? undefined,
      });
    } catch {
      toast.error('Failed to geocode address. Please try again.');
    } finally {
      setGeocoding(false);
    }
  };

  const clear = () => {
    setText('');
    onLocationChange?.('');
    setOpen(false);
    places.clear();
  };

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const count = places.predictions.length;
    if (event.key === 'ArrowDown' && count) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(index => (index + 1) % count);
    } else if (event.key === 'ArrowUp' && count) {
      event.preventDefault();
      setActiveIndex(index => (index <= 0 ? count - 1 : index - 1));
    } else if (event.key === 'Enter' && open && count) {
      event.preventDefault();
      pick(places.predictions[Math.max(0, activeIndex)]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const busy = geocoding || isLoading;
  const spinning = busy || places.loading;

  return (
    <InputContainer ref={containerRef}>
      <InputWrapper>
        <Icon>
          <FontAwesomeIcon icon={faMapMarkerAlt} />
        </Icon>
        <AddressInputField
          type="text"
          placeholder={placeholder}
          value={text}
          horizontal={horizontal}
          onChange={event => type(event.target.value)}
          onFocus={() => text.length > 2 && setOpen(true)}
          onKeyDown={keyDown}
          disabled={busy}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          role="combobox"
          aria-expanded={open && places.predictions.length > 0}
          aria-autocomplete="list"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-testid="address-input"
        />
        {text && !spinning && (
          <ClearButton onClick={clear} type="button" aria-label="Clear address">
            <FontAwesomeIcon icon={faTimes} />
          </ClearButton>
        )}
        {spinning && (
          <div className="ml-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
          </div>
        )}
      </InputWrapper>

      {open && places.predictions.length > 0 && (
        <SuggestionsContainer role="listbox" data-testid="address-suggestions">
          {places.predictions.map((item, index) => (
            <SuggestionItem
              key={item.placeId}
              role="option"
              aria-selected={index === activeIndex}
              style={
                index === activeIndex ? { background: 'var(--color-bg-secondary)' } : undefined
              }
              // mousedown, not click: the pick happens before the input loses focus.
              onMouseDown={event => {
                event.preventDefault();
                pick(item);
              }}
            >
              <SuggestionMainText>{item.mainText}</SuggestionMainText>
              {item.secondaryText && (
                <SuggestionSecondaryText>{item.secondaryText}</SuggestionSecondaryText>
              )}
            </SuggestionItem>
          ))}
        </SuggestionsContainer>
      )}
    </InputContainer>
  );
}
